import { join, map, pipe, split } from 'remeda'

/**
 * Converts a string to start case (each word capitalized with spaces).
 * Handles snake_case, camelCase, PascalCase, kebab-case, and space-separated strings.
 *
 * @example
 * startCase('hello_world') // 'Hello World'
 * startCase('helloWorld') // 'Hello World'
 * startCase('hello-world') // 'Hello World'
 * startCase('hello world') // 'Hello World'
 */
export const startCase = (str: string): string => {
  if (!str) return ''

  return pipe(
    str,
    // Split on word boundaries: underscores, hyphens, spaces, or camelCase transitions
    (s) => s.replace(/([a-z])([A-Z])/g, '$1 $2'), // Handle camelCase
    (s) => s.replace(/[_-]/g, ' '), // Replace underscores and hyphens with spaces
    (s) => s.trim(), // Remove leading/trailing whitespace
    split(/\s+/), // Split on whitespace
    map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()), // Capitalize first letter
    join(' '), // Join with spaces
  )
}

/**
 * Converts a string to kebab-case.
 * Handles camelCase, PascalCase, snake_case, and space-separated strings.
 *
 * @example
 * kebabCase('helloWorld') // 'hello-world'
 * kebabCase('HelloWorld') // 'hello-world'
 * kebabCase('hello_world') // 'hello-world'
 * kebabCase('hello world') // 'hello-world'
 */
export const kebabCase = (str: string): string => {
  if (!str) return ''

  return pipe(
    str,
    (s) => s.replace(/([a-z])([A-Z])/g, '$1-$2'), // camelCase to dash
    (s) => s.replace(/[\s_]+/g, '-'), // spaces/underscores to dash
    (s) => s.toLowerCase(),
    (s) => s.replace(/^-+|-+$/g, ''), // trim dashes
  )
}

/**
 * Capitalizes the first letter of a string and lowercases the rest.
 *
 * @example
 * capitalize('hello') // 'Hello'
 * capitalize('HELLO') // 'Hello'
 * capitalize('hELLO wORLD') // 'Hello world'
 */
export const capitalize = (str: string): string => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Escapes special RegExp characters in a string.
 * Useful for creating RegExp patterns from user input.
 *
 * @example
 * escapeRegExp('hello.world') // 'hello\\.world'
 * escapeRegExp('a*b+c?') // 'a\\*b\\+c\\?'
 * escapeRegExp('[test]') // '\\[test\\]'
 */
export const escapeRegExp = (str: string): string => {
  if (!str) return ''
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
